import { useEffect, useState } from 'react';
import { usePasswordRecovery } from 'app/hooks/usePasswordRecovery';
import { useTranslations } from 'app/hooks/useTranslations';
import { SvgUser } from 'app/icons/designSystem/SvgUser';
import { SvgSpinner } from 'app/icons/icons';
import { cn } from 'lib/utils';

interface ForgotPasswordFormProps {
  initialEmail: string;
  onBack: () => void;
}

export default function ForgotPasswordForm({
  initialEmail,
  onBack,
}: ForgotPasswordFormProps) {
  const { t } = useTranslations();
  const { isLoading, isSuccess, requestReset, loadPublicTranslations } =
    usePasswordRecovery();
  const [email, setEmail] = useState(initialEmail);

  useEffect(() => {
    loadPublicTranslations();
  }, []);

  const submit = async () => {
    if (!email.trim() || isLoading) return;
    await requestReset(email.trim());
  };

  return (
    <div
      className="flex flex-col"
      onKeyDown={event => {
        if (event.key === 'Enter') submit();
      }}
    >
      <h2 className="text-xl font-bold text-gray-800 mb-2">
        {t('forgot.title')}
      </h2>

      {isSuccess ? (
        <p className="text-sm text-green-600 my-6">{t('forgot.sent')}</p>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-6">
            {t('forgot.description')}
          </p>

          <div className="relative mb-6">
            <SvgUser className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            <input
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-md bg-gray-100 focus:outline-none focus:border-indigo-500"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder={t('login.email')}
              aria-label={t('login.email')}
            />
          </div>

          <button
            className={cn(
              'w-full bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-600 hover:to-blue-400 text-white font-bold py-3 rounded-md shadow-md transition duration-300',
              isLoading && 'opacity-70 cursor-not-allowed'
            )}
            onClick={submit}
            disabled={isLoading}
          >
            {isLoading && (
              <SvgSpinner className="inline-block w-6 h-6 mr-3 animate-spin" />
            )}
            {t('forgot.send')}
          </button>
        </>
      )}

      <button
        className="mt-4 text-sm text-blue-700 hover:underline"
        onClick={onBack}
      >
        {t('forgot.back')}
      </button>
    </div>
  );
}
